import React from 'react';
import Icon from './icons';
import { useSch } from './context';
import { addDays, dayDiff, ymdParts } from '../../../schedule/model';

const TYPE = { nat: { key: 'occ_t_nat', cls: ' sch6-ok' }, isl: { key: 'occ_t_isl', cls: '' }, gen: { key: 'occ_t_gen', cls: ' sch6-mute' } };
const FILTERS = [['all', 'occ_f_all'], ['nat', 'occ_f_nat'], ['isl', 'occ_f_isl'], ['gen', 'occ_f_gen']];
/** Countries the occasions calendar has data for (backend `occasions_data.php`); the API's own default is added when it is another one. */
export const OCCASION_COUNTRIES = ['JO', 'SA'];

/**
 * «تقويم المناسبات» (schedule v4): country, kind filters, the next 12 months grouped by month, and «مناسبة أخرى أو حملة بأيام
 * تختارها». The dates come ONLY from `GET /campaign-schedules/occasions`; picking one prefills the name and the days.
 *   occasions { country, items: [{ id, name_ar, type, start, days, approximate }], loading, failed }
 */
export default function OccasionPicker({ F, set, occasions, onCountry, pro, onUpgrade }) {
  const { t, f, now, year } = useSch();
  const filter = F.occF || 'all';
  const country = occasions.country || '';
  const options = [...OCCASION_COUNTRIES, ...(country && !OCCASION_COUNTRIES.includes(country) ? [country] : [])];
  const list = (occasions.items || []).filter((o) => filter === 'all' || o.type === filter);

  const pick = (o) => {
    if (o.days > 1 && !pro) { onUpgrade(); return; } // multi-day events are Pro
    const start = o.start < now.date ? now.date : o.start;
    set({ occ: o.id, evName: o.name_ar, start, end: addDays(o.start, o.days - 1), allDay: true });
  };

  let lastMonth = null;
  const rows = [];
  list.forEach((o) => {
    const { y, m, d } = ymdParts(o.start);
    const mkey = `${y}-${m}`;
    if (mkey !== lastMonth) { rows.push(<div key={`m${mkey}`} className="sch6-occm">{`${f.monthName(m - 1)} ${f.num(y)}`}</div>); lastMonth = mkey; }
    const T = TYPE[o.type] || TYPE.gen;
    const left = dayDiff(now.date, o.start);
    const soon = left >= 0 && left <= 45
      ? (left === 0 ? t('sch6.soon_today') : (left === 1 ? t('sch6.soon_tomorrow') : t(left > 10 ? 'sch6.soon_in_many' : 'sch6.soon_in_few', { n: f.num(left) })))
      : null;
    rows.push(
      <button type="button" key={o.id} className="sch6-occrow" aria-pressed={F.occ === o.id} onClick={() => pick(o)}>
        <span className="sch6-cal"><span className="sch6-m" style={{ display: 'block' }}>{f.monthName(m - 1)}</span><span className="sch6-d sch6-num" style={{ display: 'block' }}>{f.num(d)}</span></span>
        <span style={{ minWidth: 0 }}><b>{o.name_ar}</b>
          <span className="sch6-tags">
            <span className={`sch6-badge${T.cls}`}>{t(`sch6.${T.key}`)}</span>
            {o.approximate && <span className="sch6-badge sch6-warn">{t('sch6.occ_approx')}</span>}
            {soon && <span className="sch6-badge sch6-soon">{soon}</span>}
            {o.days > 1 && !pro && <span className="sch6-badge sch6-mute">{t('sch6.locked')}</span>}
            <span className="sch6-meta">{`${f.dtxt(o.start, true, year)}${o.days > 1 ? ` · ${t(o.days > 10 ? 'sch6.len_many' : 'sch6.len_few', { n: f.num(o.days) })}` : ''}`}</span>
          </span>
        </span>
      </button>,
    );
  });

  return (
    <div className="sch6-fld">
      <div className="sch6-occbar"><span className="sch6-lab">{t('sch6.occ_calendar')}</span>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><span className="sch6-hint">{t('sch6.occ_country')}</span>
          <select className="sch6-inp" id="sch6-occCountry" value={country} onChange={(e) => onCountry(e.target.value)}>
            {!country && <option value="">{t('sch6.occ_no_country')}</option>}
            {options.map((c) => <option key={c} value={c}>{OCCASION_COUNTRIES.includes(c) ? t(`sch6.country_${c}`) : c}</option>)}
          </select>
        </label>
      </div>
      <div className="sch6-chips">
        {FILTERS.map(([id, key]) => <button type="button" key={id} className="sch6-pchip" aria-pressed={filter === id} onClick={() => set({ occF: id })}>{t(`sch6.${key}`)}</button>)}
      </div>
      <div className="sch6-occ" id="sch6-occList">
        {occasions.loading && <p className="sch6-hint" style={{ padding: 12 }} role="status">{t('sch6.occ_loading')}</p>}
        {!occasions.loading && !occasions.failed && !list.length && <p className="sch6-hint" style={{ padding: 12 }}>{t('sch6.occ_empty')}</p>}
        {rows}
      </div>
      <button type="button" className="sch6-occother" aria-pressed={F.occ === 'custom'} onClick={() => set({ occ: 'custom', evName: '' })}>
        <svg className="sch6-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: 16, height: 16, verticalAlign: -3 }}><path d="M12 5v14M5 12h14" /></svg>{` ${t('sch6.occ_other')}`}
      </button>
      {occasions.failed && <p className="sch6-note sch6-warnnote"><Icon name="alert" /><span>{t('sch6.occ_failed')}</span></p>}
      {list.some((o) => o.approximate) && <p className="sch6-note"><Icon name="alert" /><span>{t('sch6.occ_note')}</span></p>}
    </div>
  );
}
